/**
 * Registers the website-generation CLIENT tool (`generate_website_from_blocks`)
 * with the AI tool bridge. Mounted GLOBALLY inside EventScopedAiAssistant (like
 * AiTemplateTools) — it needs only the route eventId, the event's guest field
 * names (to validate register bindings) and the builder translations.
 *
 * The model emits a section-shaped DSL; the React-free FE compiler builds a
 * GUARANTEED-valid, fully-editable website-builder Craft.js tree (via the same
 * temp.ts seed factories the builder uses) and persistWebsiteDraft saves it as a
 * normal builder DRAFT through the standard handlers — no raw HTML, no publish.
 * Soft validation failures are RETURNED as data so the model self-corrects.
 */
import { useEffect, useRef } from "react";
import { useParams } from "react-router-dom";
import { useTranslations } from "next-intl";
import { useAiToolBridge } from "@/lib/ai/ai-tool-bridge";
import { getFields } from "@/lib/api/handlers/ViewFilterHandling";
import { normalizeToFullLocale } from "@/lib/utils/locale-normalize";
import { buildWebsiteCraftJsFromBlocks } from "@/components/builders/website/data/buildWebsiteCraftJsFromBlocks";
import { persistWebsiteDraft } from "@/components/builders/website/data/persistWebsiteDraft";
import { useQueryClient } from "@tanstack/react-query";
import { invalidateWebsiteKeys } from "@/pages/event/website/components/utils/invalidateWebsiteKeys";
import { isPublicFormField } from "@/components/WebsiteBuilder/settings/publicFormFields";
import type {
  WebsiteDsl,
  WebsiteDslPage,
  WebsiteThemeBlock,
} from "@/components/builders/website/data/websiteBlocksDsl";

type GenerateWebsiteInput = {
  languages?: string[];
  theme?: WebsiteThemeBlock;
  pages?: WebsiteDslPage[];
};

export function AiWebsiteTools() {
  const { eventId = "" } = useParams<{ eventId: string }>();
  const t = useTranslations("websiteBuilder");
  const td = useTranslations("websiteBuilderDefaults");

  // Handler registered ONCE (bridge identity must stay stable for the
  // unregister race-guard); it reads the live eventId / translations via refs.
  const eventIdRef = useRef(eventId);
  eventIdRef.current = eventId;
  const tRef = useRef(t);
  tRef.current = t;
  const tdRef = useRef(td);
  tdRef.current = td;
  const queryClient = useQueryClient();
  const queryClientRef = useRef(queryClient);
  queryClientRef.current = queryClient;

  useEffect(() => {
    const { register, unregister } = useAiToolBridge.getState();

    const generateWebsiteFromBlocks = async (rawInput: unknown) => {
      const input = (rawInput ?? {}) as GenerateWebsiteInput;
      if (!Array.isArray(input.languages) || input.languages.length === 0) {
        return { ok: false, errorCode: "languagesRequired" };
      }
      // Normalize + dedupe locales; first = primary.
      const locales: string[] = [];
      for (const entry of input.languages) {
        const full = normalizeToFullLocale(entry);
        if (!full) return { ok: false, errorCode: "unknownLocale", detail: String(entry) };
        if (!locales.includes(full)) locales.push(full);
      }
      const [primaryLanguage, ...additional] = locales;

      if (!Array.isArray(input.pages) || input.pages.length === 0) {
        return { ok: false, errorCode: "noPages" };
      }

      // Resolve the event's REAL guest field names for register binding.
      const fields = await getFields("guest", eventIdRef.current);
      if (!Array.isArray(fields)) {
        return { ok: false, errorCode: "fieldsUnavailable" };
      }
      // Only variables a PUBLIC submit can write: the catalogue also carries
      // read-only guest-list columns, and the model happily binds a field
      // called "signup date" to `createdAt` — which 400s every registration
      // on the generated site (see WebsiteBuilder/settings/publicFormFields).
      const fieldNames = fields.filter(isPublicFormField).map((f) => f.name);

      const dsl: WebsiteDsl = {
        theme: input.theme,
        primaryLanguage,
        languages: additional,
        pages: input.pages,
      };

      const compiled = buildWebsiteCraftJsFromBlocks(dsl, fieldNames, {
        submitText: td("submitButton"),
        registerSubmitText: td("createAccount"),
        loginSubmitText: td("signIn"),
      });
      if (!compiled.ok) return compiled; // soft error → model self-corrects

      const result = await persistWebsiteDraft({
        eventId: eventIdRef.current,
        pages: compiled.pages,
        theme: input.theme,
        primaryLanguage,
        languages: additional,
        themeId: input.theme?.preset,
        publish: false, // draft-then-open — the user publishes from the UI
        t: tRef.current,
        td: tdRef.current,
      });
      if (!result.ok) {
        return { ok: false, errorCode: result.error };
      }
      // The website landing page can be MOUNTED while Velo generates (its
      // exists=false query never refetches on its own) — and its caches
      // outlive it either way. Client-lane tools bypass AiDataSyncBridge, so
      // invalidate here directly.
      invalidateWebsiteKeys(queryClientRef.current, eventIdRef.current);
      return {
        ok: true,
        websiteConfigId: result.websiteConfigId,
        published: result.published,
        pages: compiled.pages.map((p) => ({ slug: p.slug, pageName: p.pageName })),
        primaryLanguage,
        languages: locales,
        detail: "A website DRAFT was created. Tell the user to open the website builder to review, edit and publish it.",
        // Audit 7.12 M8: a required row's emailOptIn binding dropped.
        ...(compiled.warnings ? { warnings: compiled.warnings } : {}),
      };
    };

    register("generate_website_from_blocks", generateWebsiteFromBlocks);
    return () => {
      unregister("generate_website_from_blocks", generateWebsiteFromBlocks);
    };
  }, []);

  return null;
}
